import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { RootState } from "../redux/slices/store";
import { getData } from "../api/ApiServices";
import "../styles/StockExpiry.css";

const RMInventory: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoRecords, setHasNoRecords] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("totalValue");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [lastFilterState, setLastFilterState] = useState<any>({});
  const excludeFieldsForInventory = ["createdBy", "_id", "partDetails"];

  const columns = [
    {
      key: "partDetails",
      label: "Part ID",
      nameKey: "internalPartNo",
      render: (value: any) =>
        typeof value === "object" ? value?.internalPartNo : "N/A",
      sortable: true,
    },
    {
      key: "partDetails",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) =>
        typeof value === "object" ? value?.partName : "N/A",
      sortable: true,
    },
    { 
      key: "totalQty", 
      label: "Total Quantity", 
      sortable: true,
      isRightAlign: true,
    },
    { 
      key: "totalValue", 
      label: "Total Value", 
      sortable: true,
      isRightAlign: true,
      render: (value: number) => value ? `₹${value.toLocaleString()}` : "₹0",
    },
  ];

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  useEffect(() => {
    const hasFilterChanged = JSON.stringify(filters) !== JSON.stringify(lastFilterState);
    
    if (hasFilterChanged) {
      setIsFiltering(Object.keys(filters).length > 0);
      setLastFilterState(filters);
      
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchInventoryData();
      }
    }
  }, [filters]);

  useEffect(() => {
    fetchInventoryData();
  }, [currentPage, itemsPerPage, sortBy, sortOrder]);

  const fetchInventoryData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: any = await getData("/inwardoptions/inventory-cost", {
        page: currentPage,
        limit: itemsPerPage,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      
      console.log("API Response for RM Inventory:", response);
      
      if (response?.data) {
        setInventoryData(response.data.data);
        
        if (currentPage === 1 && !isFiltering && response.data.length > 0) {
          setOriginalData(response.data);
        }
        
        setTotalItems(response?.data?.pagination.total);
        setTotalPages(response?.data?.pagination.totalPages);
        setHasNoRecords(response.data.length === 0);
      } else {
        setInventoryData([]);
        setHasNoRecords(true);
      }
    } catch (err) {
      console.error("Error fetching RM inventory data:", err);
      setError("Failed to load inventory data. Please try again later.");
      setInventoryData([]);
      setTotalItems(0);
      setTotalPages(0);
      setHasNoRecords(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = useCallback((filters: Record<string, any>) => {
    Object.entries(filters).forEach(([key, value]) => {
      dispatch(updateFilters({ key, value: String(value) }));
    });
  }, [dispatch]);

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Inventory</span>
              <span>RM Inventory</span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="box-container">
        <div className="table-wrapper">
          <TableComponent
            columns={columns}
            data={inventoryData}
            setFilter={handleFilterChange}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={excludeFieldsForInventory} 
            context="rminventory" 
          />
          
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
            </div>
          )}
        </div>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>
    </div>
  );
};

export default RMInventory;